(function () {
  const STORAGE_KEY = 'arg.searchIndex.v1';
  const MIN_QUERY_LEN = 3;
  const MAX_DRAWER_RESULTS = 10;

  let indexCache = null;
  let indexPromise = null;

  const debug = window.__searchDebug = { events: [], state: () => ({ indexCache: !!indexCache, indexCacheSize: indexCache?.length, indexPromise: !!indexPromise }) };
  const log = (msg, data) => { debug.events.push({ t: Date.now(), msg, data }); };

  function loadFromStorage() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  async function fetchIndexPage(page) {
    log('fetch start', page);
    const res = await fetch('/collections/all?view=ingredient-index&page=' + page);
    log('fetch resp', { page, status: res.status });
    if (!res.ok) throw new Error('search-index fetch failed: ' + res.status);
    const text = await res.text();
    log('fetch body', { page, bytes: text.length });
    const parsed = JSON.parse(text);
    log('fetch parsed', { page, items: parsed.items.length, pages: parsed.pages });
    return parsed;
  }

  async function buildIndex() {
    log('build start');
    const all = [];
    let page = 1;
    while (true) {
      const data = await fetchIndexPage(page);
      all.push.apply(all, data.items);
      if (page >= data.pages) break;
      page += 1;
    }
    log('build collected', all.length);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(all));
      log('build stored');
    } catch (e) {
      log('build storage err', e.message);
    }
    return all;
  }

  function getIndex() {
    if (indexCache) return Promise.resolve(indexCache);
    if (indexPromise) return indexPromise;
    indexCache = loadFromStorage();
    if (indexCache) { log('storage hit', indexCache.length); return Promise.resolve(indexCache); }
    log('getIndex starting build');
    indexPromise = buildIndex()
      .then(idx => { indexCache = idx; indexPromise = null; log('build done', idx.length); return idx; })
      .catch(e => { log('build rejected', e.message); indexPromise = null; throw e; });
    return indexPromise;
  }

  // Queries may carry a trailing "*"; this index uses a substring test,
  // so strip it before matching.
  function stripWildcard(q) {
    return (q || '').replace(/\*+\s*$/, '');
  }

  function searchIndex(index, query) {
    const q = stripWildcard(query).trim().toLowerCase();
    if (q.length < MIN_QUERY_LEN) return [];
    return index.filter(item => item.text.indexOf(q) !== -1);
  }

  function collectExistingHandles(root) {
    const handles = new Set();
    root.querySelectorAll('a[href*="/products/"]').forEach(a => {
      const m = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      if (m) handles.add(m[1]);
    });
    return handles;
  }

  // --- Drawer integration ---

  function setupDrawer() {
    const drawer = document.querySelector('search-drawer');
    if (!drawer) return;
    const input = drawer.querySelector('input[type="search"]');
    if (!input) return;

    const warmIndex = () => { getIndex().catch(() => {}); };
    input.addEventListener('focus', warmIndex);
    input.addEventListener('input', warmIndex);

    const predictive = drawer.querySelector('predictive-search');
    if (!predictive) return;

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        injectDrawerMatches(drawer).catch(e => log('inject err', e.message));
      });
    });
    observer.observe(predictive, { childList: true, subtree: true });
    log('observer attached');
  }

  async function injectDrawerMatches(drawer) {
    const input = drawer.querySelector('input[type="search"]');
    if (!input) return;
    const query = stripWildcard(input.value).trim().toLowerCase();
    log('inject called', { query });
    if (query.length < MIN_QUERY_LEN) return;

    const resultsContainer = drawer.querySelector('div[slot="results"]');
    if (!resultsContainer) return;
    if (resultsContainer.dataset.searchInjected === query) { log('inject already done'); return; }

    const index = await getIndex();
    const matches = searchIndex(index, query);
    resultsContainer.dataset.searchInjected = query;
    log('inject matches', { count: matches.length, isConnected: resultsContainer.isConnected });
    if (matches.length === 0) return;

    const fresh = matches.filter(m => !collectExistingHandles(resultsContainer).has(m.handle));
    log('inject fresh', { count: fresh.length });
    if (fresh.length === 0) return;

    const html = fresh.slice(0, MAX_DRAWER_RESULTS).map(m => m.drawer_html).join('');
    const tabs = resultsContainer.querySelector('x-tabs.predictive-search__tabs');
    const productPanel = tabs && Array.from(tabs.querySelectorAll(':scope > [slot="content"]'))
      .find(p => p.querySelector('a[href*="/products/"]'));
    log('inject path', { hasTabs: !!tabs, hasProductPanel: !!productPanel });

    if (productPanel) {
      productPanel.insertAdjacentHTML('beforeend', html);
      log('inject appended to product panel', { isConnected: productPanel.isConnected });
    } else if (tabs) {
      const title = document.createElement('div');
      title.className = 'predictive-search__tab-item';
      title.setAttribute('slot', 'title');
      title.innerHTML = '<button type="button" class="h5">Products</button>';
      const content = document.createElement('div');
      content.className = 'predictive-search__tab-content v-stack gap-4 sm:gap-6';
      content.setAttribute('slot', 'content');
      content.innerHTML = html;
      tabs.insertBefore(title, tabs.firstChild);
      tabs.insertBefore(content, title.nextSibling);
    } else {
      const emptyState = resultsContainer.querySelector('.empty-state');
      if (emptyState) emptyState.remove();
      const wrapper = document.createElement('div');
      wrapper.className = 'predictive-search__tab-content v-stack gap-4 sm:gap-6';
      wrapper.innerHTML = html;
      resultsContainer.appendChild(wrapper);
    }
  }

  // --- Full search page integration ---

  async function setupFullPage() {
    if (location.pathname !== '/search') return;
    const params = new URLSearchParams(location.search);
    const page = parseInt(params.get('page') || '1', 10);
    if (page !== 1) return;

    const query = stripWildcard(params.get('q')).trim().toLowerCase();
    if (query.length < MIN_QUERY_LEN) return;

    const productList = document.querySelector('.product-list');
    if (!productList) return;

    const index = await getIndex();
    const matches = searchIndex(index, query);
    if (matches.length === 0) return;

    const fresh = matches.filter(m => !collectExistingHandles(productList).has(m.handle));
    if (fresh.length === 0) return;

    productList.insertAdjacentHTML('beforeend', fresh.map(m => m.card_html).join(''));
  }

  function boot() {
    setupDrawer();
    setupFullPage().catch(() => {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
